import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, FolderKanban, Users2, Calendar as CalendarIcon, Trash2, Edit3, Search } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_TR = { planning: "Planlama", active: "Aktif", on_hold: "Beklemede", completed: "Tamamlandı", cancelled: "İptal" };
const STATUS_STYLE = {
    planning: "bg-muted text-muted-foreground",
    active: "bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]",
    on_hold: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    completed: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    cancelled: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
};
const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("tr-TR") : "—"; } catch { return iso; } };
const fmtMoney = (v) => { try { return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(v || 0); } catch { return `₺${v || 0}`; } };

function ProjectModal({ open, onClose, onSaved, initial, customers, users }) {
    const isEdit = Boolean(initial?.id);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: "", customer_id: "", description: "", status: "planning",
        start_date: new Date().toISOString().slice(0, 10), end_date: "",
        budget: "", manager_id: "",
    });
    useEffect(() => {
        if (!open) return;
        if (isEdit) setForm({
            name: initial.name || "", customer_id: initial.customer_id || "",
            description: initial.description || "", status: initial.status || "planning",
            start_date: initial.start_date || "", end_date: initial.end_date || "",
            budget: initial.budget || "", manager_id: initial.manager_id || "",
        });
        else setForm({
            name: "", customer_id: "", description: "", status: "planning",
            start_date: new Date().toISOString().slice(0, 10), end_date: "",
            budget: "", manager_id: "",
        });
    }, [open, initial, isEdit]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Proje adı zorunlu.");
        setSaving(true);
        try {
            const payload = { ...form, budget: form.budget ? Number(form.budget) : undefined };
            Object.keys(payload).forEach(k => (payload[k] === "" || payload[k] === undefined) && delete payload[k]);
            const { data } = isEdit ? await api.put(`/projects/${initial.id}`, payload) : await api.post("/projects", payload);
            toast.success(isEdit ? "Proje güncellendi." : "Proje oluşturuldu.");
            onSaved && onSaved(data); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi."); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl">
                <DialogHeader><DialogTitle>{isEdit ? "Projeyi Düzenle" : "Yeni Proje"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <input required placeholder="Proje adı" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
                    <div className="grid grid-cols-2 gap-2">
                        <select className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                            <option value="">— Müşteri —</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select className={inputCls} value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
                            <option value="">— Proje Müdürü —</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                        </select>
                    </div>
                    <textarea rows={3} placeholder="Açıklama" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <div className="grid grid-cols-3 gap-2">
                        <div><label className="block text-[11px] text-muted-foreground mb-1">Başlangıç</label><input type="date" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                        <div><label className="block text-[11px] text-muted-foreground mb-1">Bitiş</label><input type="date" className={inputCls} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                        <div><label className="block text-[11px] text-muted-foreground mb-1">Bütçe (₺)</label><input type="number" className={inputCls} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
                    </div>
                    <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                        <button type="submit" disabled={saving} className="btn-brand" data-testid="project-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isEdit ? "Güncelle" : "Kaydet"}</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function ProjectsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canWrite, setCanWrite] = useState(false);
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalInitial, setModalInitial] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page_size: 500 };
            if (status) params.status = status;
            if (search) params.search = search;
            const { data } = await api.get("/projects", { params });
            setRows(data.items || []); setCanWrite(!!data.can_write);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [status, search]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (async () => {
            try {
                const [c, u] = await Promise.all([
                    api.get("/customers", { params: { page_size: 200 } }),
                    api.get("/employees", { params: { page_size: 200 } }).catch(() => ({ data: { items: [] } })),
                ]);
                setCustomers((c.data.items || []).map((x) => ({ id: x.id, name: x.name })));
                setUsers((u.data.items || []).filter((x) => x.role !== "client").map((x) => ({ id: x.id, full_name: x.full_name })));
            } catch (err) { console.debug("silent:", err); }
        })();
    }, []);

    const del = async (id) => {
        if (!window.confirm("Bu proje ve görevleri silinsin mi?")) return;
        try { await api.delete(`/projects/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Silindi."); }
        catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi."); }
    };

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight" data-testid="projects-title">Projeler</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">Müşteri bazlı tüm projeler, ekip ve bütçe yönetimi.</p>
                </div>
                {canWrite && (
                    <button onClick={() => { setModalInitial(null); setModalOpen(true); }} className="btn-brand h-11" data-testid="btn-new-project">
                        <Plus className="h-4 w-4" /> Yeni Proje
                    </button>
                )}
            </div>

            <div className="card-elevated p-3 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input placeholder="Ara..." value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-9`} />
                </div>
                <select className={`${inputCls} max-w-[180px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>
            ) : rows.length === 0 ? (
                <div className="card-elevated p-16 text-center text-muted-foreground">Kayıt yok.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rows.map((p) => (
                        <div key={p.id} className="card-elevated p-4 hover:shadow-md transition-shadow" data-testid={`project-card-${p.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <FolderKanban className="h-4 w-4 text-[hsl(var(--brand))]" />
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[p.status]}`}>{STATUS_TR[p.status]}</span>
                                    </div>
                                    <Link to={`/projects/${p.id}`} className="block text-[14.5px] font-bold mt-2 truncate hover:text-[hsl(var(--brand))]">{p.name}</Link>
                                    <div className="text-[12px] text-muted-foreground truncate">{p.customer_name || "Müşteri yok"}</div>
                                </div>
                                {canWrite && (
                                    <div className="flex gap-0.5">
                                        <button onClick={() => { setModalInitial(p); setModalOpen(true); }} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--brand))]"><Edit3 className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => del(p.id)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                )}
                            </div>
                            {p.description && <div className="text-[12px] text-muted-foreground mt-2 line-clamp-2">{p.description}</div>}
                            <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px]">
                                <div className="flex items-center gap-1.5 text-muted-foreground"><Users2 className="h-3.5 w-3.5" /> {p.manager_name || "—"}</div>
                                <div className="flex items-center gap-1.5 text-muted-foreground"><CalendarIcon className="h-3.5 w-3.5" /> {fmtDate(p.end_date)}</div>
                            </div>
                            {p.budget != null && <div className="mt-2 text-[12px] font-bold tabular-nums">{fmtMoney(p.budget)}</div>}
                        </div>
                    ))}
                </div>
            )}

            <ProjectModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={(x) => setRows((r) => { const i = r.findIndex(y => y.id === x.id); if (i >= 0) { const c = [...r]; c[i] = x; return c; } return [x, ...r]; })} initial={modalInitial} customers={customers} users={users} />
        </div>
    );
}
